<script setup lang="ts">
import { ElDatePicker } from 'element-plus'

// The range arm is `string[]`, not a mixed array: every picker here sets
// `value-format`, so a range arrives as two formatted strings — which is also the
// only array shape Element Plus accepts.
type DatePickerValue = string | number | Date | string[] | null

/**
 * Every date field: one day, a month, a year, or a range (`type="daterange"`).
 *
 * **Always set `value-format`**, so the model is the string the backend wants and not a
 * `Date` — every picker in the tree does, which is what makes the range arm `string[]`. A
 * range bound to two keys of a filter draft goes through `dateRangeModel`, which is the
 * only thing that can map one `[from, to]` pair onto two fields; the shortcut list a range
 * offers is `DATE_RANGE_SHORTCUTS`, and `isFutureDate` is the `disabled-date` that
 * stops "tomorrow".
 *
 * `teleported` is on by default, which is what a picker inside a modal needs; `block`
 * stretches it to the row, since Element Plus otherwise sizes it by type.
 */
withDefaults(
  defineProps<{
    modelValue?: DatePickerValue
    type?:
      | 'date'
      | 'datetime'
      | 'dates'
      | 'daterange'
      | 'datetimerange'
      | 'month'
      | 'year'
    /** Format of the emitted value, e.g. 'YYYY-MM-DD HH:mm:ss' */
    valueFormat?: string
    /** Format shown in the input */
    format?: string
    placeholder?: string
    startPlaceholder?: string
    endPlaceholder?: string
    rangeSeparator?: string
    clearable?: boolean
    disabled?: boolean
    /** Leave unset to inherit the size from ElConfigProvider */
    size?: 'small' | 'default' | 'large'
    /** Render the popper in body; needed inside dialogs and overflow containers */
    teleported?: boolean
    block?: boolean
  }>(),
  {
    type: 'date',
    clearable: true,
    teleported: true
  }
)

const emit = defineEmits<{
  (e: 'update:modelValue', value: DatePickerValue): void
}>()
</script>

<template>
  <ElDatePicker
    class="shared-date-picker"
    :class="{ 'shared-date-picker--block': block }"
    :model-value="modelValue ?? undefined"
    :type="type"
    :value-format="valueFormat"
    :format="format"
    :placeholder="placeholder"
    :start-placeholder="startPlaceholder"
    :end-placeholder="endPlaceholder"
    :range-separator="rangeSeparator"
    :clearable="clearable"
    :disabled="disabled"
    :size="size"
    :teleported="teleported"
    v-bind="$attrs"
    @update:model-value="emit('update:modelValue', $event)"
  />
</template>

<style lang="scss">
/* Element Plus sizes a picker by its type — 220px for a single date, 350 for a range —
   through `.el-date-editor.el-input`, so `block` has to outweigh that selector.

   The block is global on purpose. Element Plus renders the picker through its tooltip
   trigger, which clones the input's vnode and drops the scope id with it: the root element
   carries `shared-date-picker` but no `data-v-…`, so a scoped rule here matched nothing at
   all — `block` left a single date at its 220px, and a range only looked right because a
   form item is a flex row and 350px shrank into the column. `Modal.vue` is global for the
   same reason. Both classes are this component's own, so nothing else can match them, and
   naming both puts the rule above element-plus's regardless of which stylesheet lands
   first. */
.shared-date-picker.shared-date-picker--block,
.shared-date-picker.shared-date-picker--block.el-input,
.shared-date-picker.shared-date-picker--block.el-input__wrapper {
  --el-date-editor-width: 100%;
  width: 100%;
}

/* Element Plus centres the range inputs; every other control in a form
   aligns its text and placeholder left. */
.shared-date-picker .el-range-input {
  text-align: left;
}
</style>
